import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { genId, useForkRef } from "@smartsheet/lodestar-utils";
import { ThemeMode } from "@smartsheet/sds-theme-internal";
import React, { forwardRef, HTMLAttributes, ReactNode, useEffect, useMemo, useRef } from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { createFocusRingStyles } from "../../util/createFocusRingStyles";
import { withFocusToggleClassName } from "../../util/withFocusClassName";
import { useTabsContext } from "./TabsContext";
import { tabsTheme } from "./tabsTheme";

interface StyledTabPanelProps {
    themeMode: ThemeMode;
}

const StyledTabPanel = withFocusToggleClassName(
    styled("div")<StyledTabPanelProps>(({ themeMode }) =>
        createFocusRingStyles({
            theme: themeMode,
            componentRadius: tabsTheme.focusRingRadius(themeMode),
            gap: tabsTheme.focusRingGap(themeMode),
            componentBorderWidth: 0,
        })
    )
);
StyledTabPanel.displayName = "StyledTabPanel";

export interface TabPanelProps extends HTMLAttributes<HTMLElement> {
    /**
     * The children to render inside this Tab.
     */
    children: ReactNode;

    /**
     * If an id isn't provided, one will be automatically generated.
     * This is used to connect the TabPanel to its respective Tab.
     */
    id?: string;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(({ children, id, ...rest }, parentRef) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { onlyRenderActiveTabPanelChildren, registerTabPanel, selectedId, tabRegistrations } = useTabsContext();
    const localRef = useRef<HTMLDivElement>(null);
    const ref = useForkRef(localRef, parentRef);

    const finalId = useMemo(() => id ?? genId("smar--tab--panel"), [id]);

    const thisReg = tabRegistrations.find(({ panelRef }) => panelRef?.current === localRef.current);
    const isSelected = thisReg?.tabId === selectedId;

    useEffect(() => {
        if (localRef.current) {
            const { unregister } = registerTabPanel(finalId, localRef);
            return unregister;
        }
    }, [finalId, registerTabPanel]);

    return (
        <StyledTabPanel
            {...rest}
            themeMode={themeMode}
            ref={ref}
            role="tabpanel"
            tabIndex={isSelected ? 0 : -1}
            id={finalId}
            aria-labelledby={thisReg?.tabId}
            style={{
                display: isSelected ? undefined : "none",
            }}
            hidden={!isSelected}
        >
            {onlyRenderActiveTabPanelChildren ? (isSelected ? children : null) : children}
        </StyledTabPanel>
    );
});
TabPanel.displayName = "TabPanel";
